import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { LogManagerConsole } from 'Core/logManager';

export const assetAdapter = createEntityAdapter({
    selectId: (asset) => String(asset.id)
});

const assetSlice = createSlice({
    name: 'realtime_asset',
    initialState: assetAdapter.getInitialState(),
    reducers: {
        upsertManyWithRules: (state, action) => {
            const items = action.payload;

            LogManagerConsole("RT_DEBUG", "[AssetSlice] upsertManyWithRules dispatched incoming size:", items ? items.length : 0);

            if (!items || !items.length) return;

            const validUpdates = [];
            const deletedIds = [];

            for (const newItem of items) {
                const assetId = String(newItem.id || newItem.entityId);
                if (!assetId || assetId === 'undefined') continue;

                if (newItem.event === 'deleted' || newItem._deleted) {
                    deletedIds.push(assetId);
                    continue;
                }

                const existingItem = state.entities[assetId] || {};

                // Let cross-slice tags resolution be handled via Thunk/Listener or Selector
                let tags = newItem.tags && Array.isArray(newItem.tags) ? newItem.tags : existingItem.tags || [];
                let tagIds = newItem.tagIds !== undefined ? newItem.tagIds : existingItem.tagIds;

                const assetType = newItem.assetType ? {
                    entityId: newItem.assetType.entityId,
                    label: newItem.assetType.label
                } : existingItem.assetType || null;

                const assetSubtype = newItem.assetSubtype ? {
                    id: newItem.assetSubtype.id,
                    name: newItem.assetSubtype.name
                } : existingItem.assetSubtype || null;

                const objItem = {
                    ...existingItem,
                    id: Number(assetId),
                    label: newItem.label || existingItem.label,
                    unitId: newItem.unitId || existingItem.unitId,
                    assetTypeId: newItem.assetTypeId !== undefined ? newItem.assetTypeId : existingItem.assetTypeId,
                    assetType,
                    assetSubtypeId: newItem.assetSubtypeId !== undefined ? newItem.assetSubtypeId : existingItem.assetSubtypeId,
                    assetSubtype,
                    vin: newItem.vin !== undefined ? newItem.vin : existingItem.vin,
                    ecuVin: newItem.ecuVin !== undefined ? newItem.ecuVin : existingItem.ecuVin,
                    make: newItem.make !== undefined ? newItem.make : existingItem.make,
                    model: newItem.model !== undefined ? newItem.model : existingItem.model,
                    year: newItem.year !== undefined ? newItem.year : existingItem.year,
                    engine: newItem.engine !== undefined ? newItem.engine : existingItem.engine,
                    mpgHighway: newItem.mpgHighway !== undefined ? newItem.mpgHighway : existingItem.mpgHighway,
                    mpgCity: newItem.mpgCity !== undefined ? newItem.mpgCity : existingItem.mpgCity,
                    licensePlate: newItem?.licensePlate || existingItem?.licensePlate,
                    registrationState: newItem?.registrationState || existingItem?.registrationState,
                    registrationCountry: newItem?.registrationCountry || existingItem?.registrationCountry,
                    registrationExpiration: newItem?.registrationExpiration || existingItem?.registrationExpiration,
                    inspectionDeviceType: newItem?.inspectionDeviceType || existingItem?.inspectionDeviceType,
                    inspectionTagId: newItem?.inspectionTagId !== undefined ? newItem?.inspectionTagId : existingItem?.inspectionTagId,
                    powerUnitNumber: newItem?.powerUnitNumber || existingItem?.powerUnitNumber,
                    externalId: newItem?.externalId || existingItem?.externalId,
                    isActive: newItem?.isActive !== undefined ? newItem?.isActive : existingItem?.isActive,
                    chassisSerialNumber: newItem?.chassisSerialNumber || existingItem?.chassisSerialNumber,
                    engineSerialNumber: newItem?.engineSerialNumber || existingItem?.engineSerialNumber,
                    maintenanceCompany: newItem?.maintenanceCompany !== undefined ? newItem?.maintenanceCompany : existingItem?.maintenanceCompany,
                    tagIds,
                    tags
                };

                validUpdates.push(objItem);
            }

            if (deletedIds.length > 0) {
                assetAdapter.removeMany(state, deletedIds);
            }
            if (validUpdates.length > 0) {
                assetAdapter.upsertMany(state, validUpdates);
            }
        },
        removeAssets: assetAdapter.removeMany,
        clearAssets: assetAdapter.removeAll,
    }
});

export const { upsertManyWithRules, removeAssets, clearAssets } = assetSlice.actions;

export const assetSelectors = assetAdapter.getSelectors(state => state.realtime.asset);

export default assetSlice.reducer;
